import type { AbstractMesh, PBRMaterial } from '@babylonjs/core';
import { isNil } from 'es-toolkit';

import type { MetadataCache } from './types.js';

type MeshMetadata = {
  gltf?: {
    extras?: Record<string, unknown>;
  };
};

type MaterialMetadata = {
  gltf?: {
    extras?: {
      material_type?: string;
    };
  };
};

const buildMetadataCache = (
  meshes: AbstractMesh[],
  materials: PBRMaterial[],
  extrasKey?: string,
): MetadataCache => {
  const byKeyValue = new Map<string, Map<string, Set<string>>>();
  const byMaterial = new Map<string, Record<string, string>>();
  const visibilityProps = new Map<string, Record<string, boolean>>();

  for (const mesh of meshes) {
    const metadata = mesh.metadata as MeshMetadata | undefined;
    const extras = metadata?.gltf?.extras;

    if (extras && extrasKey !== undefined && extrasKey.length > 0) {
      const customProps = extras[extrasKey];
      if (!isNil(customProps) && typeof customProps === 'object') {
        if (
          customProps &&
          'visibility' in customProps &&
          typeof customProps.visibility === 'object' &&
          customProps.visibility
        ) {
          visibilityProps.set(
            mesh.id,
            customProps.visibility as Record<string, boolean>,
          );
        }

        for (const [key, value] of Object.entries(customProps ?? {})) {
          if (typeof value === 'string') {
            if (!byKeyValue.has(key)) {
              byKeyValue.set(key, new Map());
            }

            const valueMap = byKeyValue.get(key);
            if (valueMap) {
              if (!valueMap.has(value)) {
                valueMap.set(value, new Set());
              }

              const valueSet = valueMap.get(value);
              if (valueSet) {
                valueSet.add(mesh.id);
              }
            }
          }
        }
      }
    }
  }

  for (const material of materials) {
    const metadata = material.metadata as MaterialMetadata | undefined;
    const materialType = metadata?.gltf?.extras?.material_type;

    if (materialType !== undefined && materialType.length > 0) {
      const extras = { material_type: materialType };
      byMaterial.set(material.id, extras);

      const key = 'material_type';
      if (!byKeyValue.has(key)) {
        byKeyValue.set(key, new Map());
      }

      const valueMap = byKeyValue.get(key);
      if (valueMap) {
        if (!valueMap.has(materialType)) {
          valueMap.set(materialType, new Set());
        }

        const valueSet = valueMap.get(materialType);
        if (valueSet) {
          valueSet.add(material.id);
        }
      }
    }
  }

  return { byKeyValue, byMaterial, visibilityProps };
};

const getMeshesByMetadata = (
  cache: MetadataCache,
  key: string,
  value: string,
): Set<string> => cache.byKeyValue.get(key)?.get(value) ?? new Set();

const getMaterialsByMetadata = (
  cache: MetadataCache,
  key: string,
  value: string,
): Set<string> => cache.byKeyValue.get(key)?.get(value) ?? new Set();

export { buildMetadataCache, getMaterialsByMetadata, getMeshesByMetadata };
